import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Crown,
  Shield,
  LayoutGrid,
  Settings,
  CreditCard,
  Lock,
  CheckCircle2,
  Key,
  Receipt,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { supabase } from "../lib/supabaseClient";
import MicrosoftProfileCard from "../components/MicrosoftProfileCard";
import ToolsSection from "../components/ToolsSection";

const DASH_T = {
  en: {
    title: "Welcome back",
    signedAs: "Signed in as",
    subscription: "Subscription",
    managePlan: "Manage your plan and billing.",
    upgradePlus: "Upgrade to Plus",
    upgradePremium: "Upgrade to Premium",
    manageSub: "Manage subscription",
    access: "Access",
    yourTools: "Your tools",
    tools: "Tools",
    discord: "Discord",
    quickActions: "Quick actions",
    continue: "Continue",
    timeTracker: "Time Tracker",
    pricing: "Pricing",
    security: "Password & Security",
    newPass: "New password",
    updatePass: "Update password",
    saving: "Saving...",
    billing: "Billing history",
    noInvoices: "No invoices yet.",
    viewInvoice: "View invoice",
    redirecting: "Redirecting...",
  },
  es: {
    title: "Bienvenido de vuelta",
    signedAs: "Conectado como",
    subscription: "Suscripción",
    managePlan: "Gestiona tu plan y facturación.",
    upgradePlus: "Mejorar a Plus",
    upgradePremium: "Mejorar a Premium",
    manageSub: "Gestionar suscripción",
    access: "Acceso",
    yourTools: "Tus herramientas",
    tools: "Herramientas",
    discord: "Discord",
    quickActions: "Acciones rápidas",
    continue: "Continuar",
    timeTracker: "Registrador de tiempo",
    pricing: "Planes",
    security: "Contraseña y seguridad",
    newPass: "Nueva contraseña",
    updatePass: "Actualizar contraseña",
    saving: "Guardando...",
    billing: "Historial de facturación",
    noInvoices: "Sin facturas aún.",
    viewInvoice: "Ver factura",
    redirecting: "Redirigiendo...",
  },
};

type Plan = "free" | "plus" | "premium";

function planLabel(plan: Plan) {
  if (plan === "plus") return "uCloud360 Plus";
  if (plan === "premium") return "uCloud360 Premium";
  return "Free";
}

function planBadgeClasses(plan: Plan) {
  if (plan === "premium") return "bg-black text-white";
  if (plan === "plus") return "bg-gray-900 text-white";
  return "bg-gray-100 text-gray-900";
}

function getPriceId(plan: Exclude<Plan, "free">) {
  const plus = process.env.REACT_APP_STRIPE_PRICE_PLUS;
  const premium = process.env.REACT_APP_STRIPE_PRICE_PREMIUM;
  return plan === "plus" ? plus : premium;
}

export default function DashboardPage() {
  const { user, plan, role, refreshUser, session } = useAuth() as any;
  const hasMicrosoftToken =
    session?.provider_token && session?.user?.app_metadata?.provider === "azure";
  const { lang } = useLang();
  const t = DASH_T[lang];
  const [loadingPlan, setLoadingPlan] = useState<Plan | null>(null);
  const [password, setPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const navigate = useNavigate();

  const currentPlan: Plan = (plan || "free") as Plan;

  /* 🔁 Detect Stripe success */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      refreshUser();
      params.delete("checkout");
      navigate("/dashboard", { replace: true });
    }
  }, [refreshUser, navigate]);

  /* 📄 Billing history — loaded from Stripe webhook when available */
  useEffect(() => { setInvoices([]) }, [])

  const entitlements = useMemo(() => {
    return {
      tools: true,
      discord: currentPlan === "plus" || currentPlan === "premium",
      mentorship: false,
    };
  }, [currentPlan]);

  const startCheckout = async (targetPlan: Exclude<Plan, "free">) => {
    if (!user?.id || !user?.email) {
      alert("You must be logged in to upgrade.");
      return;
    }

    const priceId = getPriceId(targetPlan);
    if (!priceId) {
      alert(
        `Missing priceId env var for ${targetPlan}. Set REACT_APP_STRIPE_PRICE_${targetPlan.toUpperCase()} and redeploy.`
      );
      return;
    }

    try {
      setLoadingPlan(targetPlan);

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceId,
          userId: user.id,
          email: user.email,
          origin: window.location.origin,
        },
      });

      if (error || !data?.url) {
        throw error || new Error("Missing checkout URL");
      }

      window.location.href = data.url;
    } catch (e: any) {
      alert(e.message || "Error starting checkout");
    } finally {
      setLoadingPlan(null);
    }
  };

  /* 🔐 Change password */
  const updatePassword = async () => {
    if (password.length < 8) {
      alert("Password must be at least 8 characters.");
      return;
    }

    try {
      setSavingPassword(true);
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      alert("Password updated successfully.");
      setPassword("");
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      {/* Tools */}
      <ToolsSection className="mb-10" />

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm text-gray-500">Dashboard</div>
          <h1 className="text-3xl font-semibold tracking-tight">Welcome back</h1>
          <div className="mt-2 text-sm text-gray-600">
            {t.signedAs} <span className="font-medium">{user?.email}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-sm ${planBadgeClasses(currentPlan)}`}>
            <Crown className="inline h-4 w-4 mr-1" />
            {planLabel(currentPlan)}
          </span>

          <span className="rounded-full px-3 py-1 text-sm border bg-white dark:bg-neutral-900 dark:text-white dark:border-gray-700">
            <Shield className="inline h-4 w-4 mr-1" />
            {role || "user"}
          </span>
        </div>
      </div>

      {/* Microsoft 365 Profile */}
      {hasMicrosoftToken && (
        <div className="mt-8">
          <MicrosoftProfileCard />
        </div>
      )}

      {/* Main grid */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Subscription */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6 bg-white dark:bg-neutral-900 shadow-sm">
          <div className="flex justify-between">
            <div>
              <div className="text-sm text-gray-500">Subscription</div>
              <div className="text-xl font-semibold mt-1">
                {planLabel(currentPlan)}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Manage your plan and billing.
              </p>
            </div>
            <CreditCard className="h-5 w-5 text-gray-600" />
          </div>

          {currentPlan === "free" && (
            <div className="mt-5 flex flex-col gap-2">
              <button
                onClick={() => startCheckout("plus")}
                className="rounded-xl bg-[#2563EB] text-white hover:bg-[#1d4ed8] px-4 py-2 text-sm"
              >
                {loadingPlan === "plus" ? t.redirecting : t.upgradePlus}
              </button>
              <button
                onClick={() => startCheckout("premium")}
                className="rounded-xl border px-4 py-2 text-sm"
              >
                {t.upgradePremium}
              </button>
            </div>
          )}

          {currentPlan !== "free" && (
            <Link
              to="/pricing"
              className="mt-5 inline-flex items-center text-sm font-medium"
            >
              {t.manageSub} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          )}
        </div>

        {/* Access */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6 bg-white dark:bg-neutral-900 shadow-sm">
          <div className="flex justify-between">
            <div>
              <div className="text-sm text-gray-500">Access</div>
              <div className="text-xl font-semibold mt-1">{t.yourTools}</div>
            </div>
            <LayoutGrid className="h-5 w-5 text-gray-600" />
          </div>

          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex justify-between">
              {t.tools} <CheckCircle2 className="h-4 w-4" />
            </li>
            <li className="flex justify-between">
              {t.discord}
              {entitlements.discord ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Lock className="h-4 w-4" />
              )}
            </li>
          </ul>
        </div>

        {/* Quick actions */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6 bg-white dark:bg-neutral-900 shadow-sm">
          <div className="flex justify-between">
            <div>
              <div className="text-sm text-gray-500">Quick actions</div>
              <div className="text-xl font-semibold mt-1">{t.continue}</div>
            </div>
            <Settings className="h-5 w-5 text-gray-600" />
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <Link to="/app/time-tracker" className="border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2 text-sm dark:text-white">
              {t.timeTracker}
            </Link>
            <Link to="/pricing" className="border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2 text-sm dark:text-white">
              {t.pricing}
            </Link>
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="mt-10 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 bg-white dark:bg-neutral-900 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Key className="h-5 w-5" />
          <h2 className="text-lg font-semibold">{t.security}</h2>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="password"
            placeholder={t.newPass}
            className="border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2 text-sm w-full bg-white dark:bg-black text-black dark:text-white"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            onClick={updatePassword}
            disabled={savingPassword}
            className="rounded-xl bg-black text-white px-4 py-2 text-sm"
          >
            {savingPassword ? t.saving : t.updatePass}
          </button>
        </div>
      </div>

      {/* Billing history */}
      <div className="mt-10 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 bg-white dark:bg-neutral-900 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="h-5 w-5" />
          <h2 className="text-lg font-semibold">{t.billing}</h2>
        </div>

        {invoices.length === 0 ? (
          <p className="text-sm text-gray-500">No invoices yet.</p>
        ) : (
          <div className="space-y-3">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="flex justify-between items-center border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm"
              >
                <div>
                  <div className="font-medium">
                    ${(inv.amount / 100).toFixed(2)} {inv.currency?.toUpperCase()}
                  </div>
                  <div className="text-gray-500">
                    {new Date(inv.created_at).toLocaleDateString()}
                  </div>
                </div>
                <a
                  href={inv.invoice_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm underline"
                >
                  {t.viewInvoice}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
